import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import { apiGet, apiPost } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { logOut } from '../../auth';

interface ProfileData {
  name?: string;
  nationality?: string;
  city?: string;
}

const REASONS = [
  'I no longer need the app',
  'Privacy concerns',
  'Found a better alternative',
  'Too many notifications',
  'App is too complicated',
  'Technical issues',
  'Other',
];

export default function AppSettingsDeleteAccount() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ data: ProfileData }>('/api_profile', undefined, true)
      .then(res => setProfileData(res.data))
      .catch(() => {});
  }, []);

  const community = profileData?.nationality && profileData?.city
    ? `${profileData.nationality} in ${profileData.city}`
    : '';

  const sendFeedback = async () => {
    try {
      await fetch('https://api.sahayy.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: profileData?.name || 'Unknown User',
          email: 'care@sahayy.com',
          subject: '[Web] Account Deletion Feedback',
          message: `User is deleting their account.\n\nName: ${profileData?.name || 'Not provided'}\nEmail: ${userProfile?.email || 'Not provided'}\nCommunity: ${community || 'Not provided'}\n\nReason: ${selectedReason}\n\nAdditional Details: ${details || 'None provided'}`,
        }),
      });
    } catch {
      // Don't block deletion if feedback fails
    }
  };

  const handleDelete = async () => {
    if (!selectedReason) {
      setError('Please select a reason for deleting your account.');
      return;
    }

    const confirmed = window.confirm(
      'This action cannot be undone. Your account and all data will be permanently deleted. Are you sure?'
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setError(null);
    try {
      await sendFeedback();
      await apiPost('/api_delete_profile', {}, true);
      await logOut();
      alert('Your account has been successfully deleted.');
      navigate('/groups');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppNav />

      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '2rem 1rem 6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <button onClick={() => navigate('/settings')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#111', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}>← Settings</button>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <p style={{ fontSize: '3.5rem', margin: '0 0 0.75rem' }}>😢</p>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111', margin: '0 0 0.5rem', letterSpacing: '-0.02em' }}>
            We're sorry to see you go
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
            Help us improve by telling us why you're leaving
          </p>
        </div>

        {/* Reasons */}
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ padding: '1rem 1rem 0.5rem' }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', color: '#111' }}>Reason for leaving *</p>
          </div>
          {REASONS.map((reason, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedReason(reason)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none',
                cursor: 'pointer', textAlign: 'left',
                backgroundColor: selectedReason === reason ? '#fff5f5' : 'transparent',
                borderTop: idx === 0 ? 'none' : '1px solid #f0f2f7',
              }}
              onMouseEnter={e => { if (selectedReason !== reason) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f9fafb'; }}
              onMouseLeave={e => { if (selectedReason !== reason) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
            >
              <span style={{ fontSize: '1rem', color: selectedReason === reason ? '#FF385C' : '#d1d5db' }}>
                {selectedReason === reason ? '⦿' : '○'}
              </span>
              <span style={{ fontSize: '0.95rem', color: '#111', fontWeight: selectedReason === reason ? 600 : 400 }}>
                {reason}
              </span>
            </button>
          ))}
        </div>

        {/* Details */}
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '1rem', marginBottom: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.95rem', color: '#111', marginBottom: '0.75rem' }}>
            Additional details <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            value={details}
            onChange={e => setDetails(e.target.value)}
            placeholder="Tell us more about your experience..."
            style={{
              width: '100%', minHeight: '100px', padding: '0.75rem',
              border: '1.5px solid #e5e7eb', borderRadius: '8px',
              fontSize: '0.9rem', color: '#111', resize: 'vertical',
              fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
          />
        </div>

        {error && (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <button
          onClick={handleDelete}
          disabled={isDeleting}
          style={{
            display: 'block', width: '100%', padding: '0.875rem',
            backgroundColor: '#dc2626', border: 'none', borderRadius: '10px',
            color: '#fff', fontSize: '0.95rem', fontWeight: 600,
            cursor: isDeleting ? 'default' : 'pointer', opacity: isDeleting ? 0.6 : 1,
            marginBottom: '0.75rem',
          }}
        >
          {isDeleting ? 'Deleting Account...' : 'Delete My Account'}
        </button>

        <button
          onClick={() => navigate('/settings')}
          style={{
            display: 'block', width: '100%', padding: '0.875rem',
            background: 'none', border: 'none',
            color: '#FF385C', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
